import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { FormField } from '../../components/ui/FormField';
import { FeedbackModal } from '../../components/ui/FeedbackModal';
import { offersService, type Offer } from '../../services/offers.service';
import { formatSAR, formatDate } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';

type Channel = 'sms' | 'email';

export const SendOfferPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState<Channel>('sms');
  const [contact, setContact] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    if (!id) return;
    offersService
      .get(id)
      .then((o) => {
        setOffer(o);
        // Pre-fill contact from customer info
        setContact(
          channel === 'email'
            ? (o.customer?.contactEmail ?? '')
            : (o.customer?.contactMobile ?? ''),
        );
      })
      .catch(() => showToast('Failed to load offer', 'error'))
      .finally(() => setLoading(false));
  }, [id, showToast]);

  useEffect(() => {
    if (!offer) return;
    setContact(
      channel === 'email'
        ? (offer.customer?.contactEmail ?? '')
        : (offer.customer?.contactMobile ?? ''),
    );
  }, [channel, offer]);

  const handleGenerateLink = async () => {
    if (!id) return;
    setGenerating(true);
    try {
      const { url } = await offersService.generateLink(id, { channel, contact });
      setLink(url);
      showToast('Customer link generated and offer sent!', 'success');
      setShowFeedback(true);
    } catch {
      showToast('Failed to generate customer link', 'error');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ color: '#6c757d' }}>Loading…</span>
      </div>
    );
  }

  if (!offer) return null;

  const customerIncomplete = !offer.customer?.nameEn || (!offer.customer?.contactMobile && !offer.customer?.contactEmail);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Send Offer" subtitle={offer.offerNumber} />
      <main style={{ flex: 1, padding: '28px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '720px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Offer summary card */}
          <div className="card" style={{ padding: '24px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <div>
                <div style={{ fontSize: '20px', fontWeight: 800 }}>{offer.offerNumber}</div>
                <div style={{ fontSize: '13px', color: '#6c757d' }}>
                  Created {formatDate(offer.createdAt)}
                </div>
              </div>
              <Badge label={offer.status} />
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                fontSize: '14px',
              }}
            >
              <div>
                <span style={{ color: '#6c757d' }}>Customer: </span>
                {offer.customer?.nameEn ?? '—'}
              </div>
              <div>
                <span style={{ color: '#6c757d' }}>Total: </span>
                <strong>{formatSAR(offer.total)}</strong>
              </div>
              <div>
                <span style={{ color: '#6c757d' }}>Products: </span>
                {offer.items?.length ?? 0} item(s)
              </div>
              <div>
                <span style={{ color: '#6c757d' }}>Auth. Person: </span>
                {offer.authorizedPerson?.nameEn ?? '—'}
              </div>
            </div>
          </div>

          {/* Customer info incomplete warning */}
          {customerIncomplete && (
            <div
              style={{
                background: '#fff7ed',
                border: '1px solid #fed7aa',
                borderRadius: '10px',
                padding: '16px 18px',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
              }}
            >
              <span style={{ fontSize: '18px', flexShrink: 0 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#92400e', marginBottom: '6px' }}>
                  Customer information is incomplete
                </div>
                <p style={{ fontSize: '13px', color: '#92400e', margin: '0 0 10px' }}>
                  Please fill in the customer details before sending this offer.
                </p>
                <button
                  onClick={() => navigate(`/offers/wizard/customer`)}
                  style={{
                    padding: '7px 16px',
                    background: '#f97316',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Complete Customer Info
                </button>
              </div>
            </div>
          )}

          {/* Channel selection + send */}
          {!link ? (
            <div className="card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontSize: '15px', marginBottom: '4px' }}>
                Send Customer Link
              </h3>
              <p style={{ color: '#6c757d', fontSize: '13px' }}>
                Choose how to deliver the offer link to the customer. They will use it to
                review and accept or decline the offer.
              </p>

              <div style={{ display: 'flex', gap: '12px' }}>
                {(['sms', 'email'] as Channel[]).map((ch) => (
                  <label
                    key={ch}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      border: `1.5px solid ${channel === ch ? 'var(--p)' : 'var(--border)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      flex: 1,
                      background: channel === ch ? 'var(--p-light)' : 'var(--surface)',
                      fontWeight: channel === ch ? 700 : 400,
                      fontSize: '13.5px',
                    }}
                  >
                    <input
                      type="radio"
                      name="channel"
                      value={ch}
                      checked={channel === ch}
                      onChange={() => setChannel(ch)}
                      style={{ accentColor: 'var(--p)' }}
                    />
                    {ch === 'sms' ? '📱 SMS' : '✉️ Email'}
                  </label>
                ))}
              </div>

              <FormField
                label={channel === 'sms' ? 'Mobile Number' : 'Email Address'}
                required
                type={channel === 'email' ? 'email' : 'tel'}
                placeholder={channel === 'sms' ? '+9665XXXXXXXX' : 'customer@company.sa'}
                value={contact}
                onChange={(e) => setContact(e.target.value)}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                <Button variant="ghost" onClick={() => navigate('/my-offers')}>
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateLink}
                  loading={generating}
                  disabled={!contact || customerIncomplete}
                >
                  Generate & Send Link
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </main>

      {id && (
        <FeedbackModal
          isOpen={showFeedback}
          offerId={id}
          offerNumber={offer?.offerNumber}
          link={link ?? undefined}
          contact={contact}
          channel={channel}
          authorizedPersonName={offer?.authorizedPerson?.nameEn ?? undefined}
          onSubmit={() => { setShowFeedback(false); navigate('/my-offers'); }}
        />
      )}
    </div>
  );
};
